import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dropdown,
  Loader,
  Message,
  Modal,
  Table,
} from 'semantic-ui-react';
import {
  fetchMissions,
  fetchMissionsByCategory,
  linkCategoryMission,
  unlinkCategoryMission,
} from '../api';
import { defaultPageRequest, LoyaltyCategory, Mission } from '../types';
import SuccessDataSaveNotify from '../../Cards/SuccessDataSaveNotify';

type Props = {
  open: boolean;
  category: LoyaltyCategory | null;
  onClose: () => void;
};

/**
 * Category↔mission list rows use `id` = link row id and `missionId` = mission id.
 * Plain /missions/all rows use `id` = mission id (no missionId).
 * Unlink URL must always use the mission id.
 */
export const resolveMissionId = (m: Mission | any): number | null => {
  if (!m) {
    return null;
  }
  if (m.missionId !== undefined && m.missionId !== null && m.missionId !== '') {
    return Number(m.missionId);
  }
  if (m.mission && m.mission.id !== undefined && m.mission.id !== null) {
    return Number(m.mission.id);
  }
  // Plain Mission DTO from /missions/all
  if (
    m.id !== undefined &&
    m.id !== null &&
    m.missionId === undefined &&
    (m.targetValue !== undefined || m.reward !== undefined || m.type !== undefined)
  ) {
    return Number(m.id);
  }
  return null;
};

export const resolveMissionName = (m: Mission | any): string => {
  if (!m) {
    return '';
  }
  return m.missionName || (m.mission && m.mission.name) || m.name || '';
};

const resolveMissionActive = (m: Mission | any): boolean => {
  if (!m) {
    return true;
  }
  if (m.active !== undefined && m.active !== null) {
    return m.active !== false;
  }
  if (m.mission && m.mission.active !== undefined && m.mission.active !== null) {
    return m.mission.active !== false;
  }
  return true;
};

const CategoryMissionsModal = ({ open, category, onClose }: Props) => {
  const [linked, setLinked] = useState<Mission[]>([]);
  const [allMissions, setAllMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedMissionId, setSelectedMissionId] = useState<number | null>(
    null
  );
  const [busy, setBusy] = useState(false);
  const [pendingUnlinkId, setPendingUnlinkId] = useState<number | null>(null);

  const categoryId = category && category.id;

  const load = useCallback(() => {
    if (categoryId === undefined || categoryId === null) {
      return;
    }
    setLoading(true);
    setError('');
    setPendingUnlinkId(null);
    const pageAll = { ...defaultPageRequest, size: 500, sortBy: 'id' };
    Promise.all([
      fetchMissionsByCategory(categoryId, pageAll),
      fetchMissions(pageAll),
    ])
      .then(([linkedRes, allRes]) => {
        setLinked(Array.isArray(linkedRes.items) ? linkedRes.items : []);
        setAllMissions(Array.isArray(allRes.items) ? allRes.items : []);
      })
      .catch(() => {
        setError('Не удалось загрузить связи миссий');
        setLinked([]);
        setAllMissions([]);
      })
      .finally(() => setLoading(false));
  }, [categoryId]);

  useEffect(() => {
    if (open && categoryId !== undefined && categoryId !== null) {
      load();
      setSelectedMissionId(null);
      setPendingUnlinkId(null);
    }
  }, [open, categoryId, load]);

  const linkedMissionIds: { [key: number]: boolean } = {};
  linked.forEach((m) => {
    const mid = resolveMissionId(m);
    if (mid !== null) {
      linkedMissionIds[mid] = true;
    }
  });

  const availableOptions = allMissions
    .filter((m) => {
      const mid = resolveMissionId(m);
      return (
        mid !== null &&
        !linkedMissionIds[mid] &&
        m.active !== false
      );
    })
    .map((m) => {
      const mid = resolveMissionId(m) as number;
      const displayName = resolveMissionName(m);
      return {
        key: mid,
        value: mid,
        text: `${displayName} (id ${mid}, цель ${m.targetValue}, +${m.reward})`,
      };
    });

  const doLink = () => {
    if (
      categoryId === undefined ||
      categoryId === null ||
      selectedMissionId === null
    ) {
      return;
    }
    setBusy(true);
    setError('');
    linkCategoryMission(categoryId, selectedMissionId)
      .then(() => {
        SuccessDataSaveNotify('Связь добавлена');
        setSelectedMissionId(null);
        load();
      })
      .catch((err) => {
        const msg =
          (err &&
            err.response &&
            err.response.data &&
            err.response.data.message) ||
          'Не удалось привязать миссию';
        setError(String(msg));
      })
      .finally(() => setBusy(false));
  };

  const doUnlink = (missionId: number) => {
    if (categoryId === undefined || categoryId === null) {
      return;
    }
    setBusy(true);
    setError('');
    unlinkCategoryMission(categoryId, missionId)
      .then(() => {
        setPendingUnlinkId(null);
        SuccessDataSaveNotify('Связь удалена');
        setLinked((prev) =>
          prev.filter((m) => resolveMissionId(m) !== missionId)
        );
        load();
      })
      .catch((err) => {
        setPendingUnlinkId(null);
        const msg =
          (err &&
            err.response &&
            err.response.data &&
            err.response.data.message) ||
          'Не удалось отвязать миссию';
        setError(String(msg));
      })
      .finally(() => setBusy(false));
  };

  return (
    <Modal open={open} onClose={onClose} size="small" closeOnDimmerClick={!busy}>
      <Modal.Header>
        Миссии категории «{category ? category.name : ''}»
      </Modal.Header>
      <Modal.Content>
        {loading ? (
          <Loader active inline="centered" />
        ) : (
          <React.Fragment>
            {error ? (
              <Message negative onDismiss={() => setError('')}>
                <Message.Header>{error}</Message.Header>
                <Button size="small" onClick={load} style={{ marginTop: 8 }}>
                  Обновить список
                </Button>
              </Message>
            ) : null}

            <Table celled compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>ID миссии</Table.HeaderCell>
                  <Table.HeaderCell>Название</Table.HeaderCell>
                  <Table.HeaderCell>Цель</Table.HeaderCell>
                  <Table.HeaderCell>Награда</Table.HeaderCell>
                  <Table.HeaderCell>Активна</Table.HeaderCell>
                  <Table.HeaderCell>Действия</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {linked.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={6} textAlign="center">
                      Нет связанных миссий
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  linked.map((m) => {
                    const mid = resolveMissionId(m);
                    const confirming =
                      pendingUnlinkId !== null && pendingUnlinkId === mid;
                    return (
                      <Table.Row key={mid !== null ? mid : String(m.id)}>
                        <Table.Cell>{mid !== null ? mid : '—'}</Table.Cell>
                        <Table.Cell>{resolveMissionName(m)}</Table.Cell>
                        <Table.Cell>{m.targetValue}</Table.Cell>
                        <Table.Cell>{m.reward}</Table.Cell>
                        <Table.Cell>
                          {resolveMissionActive(m) ? 'Да' : 'Нет'}
                        </Table.Cell>
                        <Table.Cell>
                          {confirming ? (
                            <React.Fragment>
                              <Button
                                size="mini"
                                negative
                                disabled={busy || mid === null}
                                loading={busy}
                                onClick={() => mid !== null && doUnlink(mid)}
                              >
                                Подтвердить
                              </Button>
                              <Button
                                size="mini"
                                disabled={busy}
                                onClick={() => setPendingUnlinkId(null)}
                                style={{ marginLeft: 4 }}
                              >
                                Отмена
                              </Button>
                            </React.Fragment>
                          ) : (
                            <Button
                              size="mini"
                              negative
                              basic
                              disabled={busy || mid === null}
                              onClick={() =>
                                mid !== null && setPendingUnlinkId(mid)
                              }
                            >
                              Отвязать
                            </Button>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })
                )}
              </Table.Body>
            </Table>

            <div style={{ marginTop: 16 }}>
              <label style={{ display: 'block', marginBottom: 6 }}>
                Добавить миссию
              </label>
              <p style={{ color: '#888', fontSize: 12, marginTop: 0 }}>
                В списке только активные миссии, ещё не привязанные к этой
                категории
                {allMissions.length
                  ? ` (всего: ${allMissions.length}, доступно: ${availableOptions.length})`
                  : ''}
              </p>
              <Dropdown
                placeholder="Выберите миссию"
                fluid
                selection
                search
                clearable
                options={availableOptions}
                value={selectedMissionId as any}
                onChange={(_e, { value }) =>
                  setSelectedMissionId(
                    value === null || value === undefined || value === ''
                      ? null
                      : Number(value)
                  )
                }
                noResultsMessage="Нет доступных миссий для добавления"
              />
              <Button
                primary
                style={{ marginTop: 10 }}
                disabled={busy || selectedMissionId === null}
                loading={busy}
                onClick={doLink}
              >
                Привязать
              </Button>
            </div>
          </React.Fragment>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={busy}>
          Закрыть
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default CategoryMissionsModal;
